/* eslint-disable max-lines */
/* eslint-disable max-lines-per-function */
import React, { useState, useEffect, useCallback } from 'react';
import {
  Routes,
  Route,
  useNavigate
} from 'react-router-dom';

// Form submission
import onEmailSubmit from './onEmailSubmit';
import onPasswordSubmit from './onPasswordSubmit';

// Components
import LoginEmailPage from '../../pages/LoginEmailPage';
import LoginPasswordPage from '../../pages/LoginPasswordPage';
import CaptchaFeature, { extractCaptchaKey } from '../../shared/CaptchaFeature';
import DefaultLoginRedirect from 'src/viewLayer/components/redirects/DefaultLoginRedirect';

// Hooks
import useHandleSessionRequest from './useHandleSessionRequest';
import useInputState from '../../hooks/useInputState';
import useHandleAuthTokenRequest from './useHandleAuthTokenRequest';
import { usePasswordPagePath } from '../../../navigation';
import { useRouteParamsContext } from 'src/viewLayer/components/contexts/RouteParamsContext';
import { useAppConfigContext } from 'src/viewLayer/components/contexts/AppConfigContext';

const EMAIL_ROUTE_PATH = 'email';
const PASSWORD_ROUTE_PATH = 'password';

/*
 * Parent component of the login sub-pages, carries out the login operations.
 */
export default function EmailLoginFlow(props) {
  const navigate = useNavigate();
  const passwordPagePath = usePasswordPagePath();
  const { redirectTo } = useRouteParamsContext();
  const { devicePath } = useAppConfigContext();

  const initEmail = {
    errorKey: '',
    inputValue: props.localStorageEmail || email || '',
    isFocused: true
  };

  // State
  const {
    inputValue: email,
    errorKey: emailErrorKey,
    isFocused: emailIsFocused,
    setInputValue: setEmail,
    setErrorKey: setEmailErrorKey,
    setFocus: setEmailFocus,
    resetInputState: resetEmailState
  } = useInputState(initEmail);

  const {
    inputValue: password,
    errorKey: passwordErrorKey,
    isFocused: passwordIsFocused,
    setInputValue: setPassword,
    setErrorKey: setPasswordErrorKey,
    setFocus: setPasswordFocus,
    resetInputState: resetPasswordState
  } = useInputState();

  // Endpoints
  const {
    makeAuthSessionRequest,
    authSessionRequestInProgress,
    sessionCode,
    captchaConfig
  } = useHandleSessionRequest(email);

  const getInitialCaptchaCompleteValue = useCallback(
    () => !extractCaptchaKey({ captchaConfig }),
    [captchaConfig]
  );

  const [captchaComplete, setCaptchaComplete] = useState(getInitialCaptchaCompleteValue());

  useEffect(() => {
    setCaptchaComplete(getInitialCaptchaCompleteValue());
  }, [sessionCode, getInitialCaptchaCompleteValue]);

  const {
    makeAuthTokenRequest,
    authTokenRequestInProgress
  } = useHandleAuthTokenRequest({ sessionCode, email, password });

  // Form submission props
  const emailSubmitParams = {
    email,
    makeAuthSessionRequest,
    setEmailErrorKey,
    resetPasswordState,
    passwordPagePath
  };

  const passwordSubmitParams = {
    makeAuthTokenRequest,
    makeAuthSessionRequest,
    devicePath,
    redirectTo,
    setPasswordErrorKey
  };

  // JSX
  const captchaFeature = (
    <CaptchaFeature
      captchaConfig={captchaConfig}
      setCaptchaComplete={setCaptchaComplete}
      sessionCode={sessionCode}
    />
  );

  return (
    <Routes>
      <Route
        path={ EMAIL_ROUTE_PATH }
        element={
          <LoginEmailPage
            appType={props.appType}
            email={email}
            errorKey={emailErrorKey}
            isFocused={emailIsFocused}
            path={ EMAIL_ROUTE_PATH }
            setFocus={setEmailFocus}
            inProgress={authSessionRequestInProgress}
            onChange={setEmail}
            onReset={resetEmailState}
            onSubmit={ (event) => {
              event.preventDefault();
              onEmailSubmit(emailSubmitParams, navigate);
            }}
          />
        }
      />
      <Route
        path={ PASSWORD_ROUTE_PATH }
        element={
          <LoginPasswordPage
            appType={props.appType}
            password={password}
            errorKey={passwordErrorKey}
            isFocused={passwordIsFocused}
            path={ PASSWORD_ROUTE_PATH }
            setFocus={setPasswordFocus}
            email={email}
            captchaFeature={captchaFeature}
            captchaComplete={captchaComplete}
            inProgress={authTokenRequestInProgress}
            onChange={ setPassword }
            onSubmit={ (event) => {
              event.preventDefault();
              onPasswordSubmit(passwordSubmitParams);
            }}
          />
        }
      />
      <Route
        path="*"
        element={ <DefaultLoginRedirect /> }
      />
    </Routes>
  );
};
